import { useEffect, useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { supabase } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { formatDistanceToNow } from 'date-fns';
import { Loader2, User, Bot } from 'lucide-react';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface Conversation {
  id: string;
  session_id: string;
  messages: Message[];
  appointment_requested: boolean;
  appointment_id: string | null;
  user_info: any;
  started_at: string;
  last_activity_at: string;
  status: string;
}

interface ChatConversationDrawerProps {
  conversationId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ChatConversationDrawer({
  conversationId,
  open,
  onOpenChange,
}: ChatConversationDrawerProps) {
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (conversationId && open) {
      fetchConversation();
    }
  }, [conversationId, open]);

  const fetchConversation = async () => {
    if (!conversationId) return;
    
    setIsLoading(true);
    const { data, error } = await supabase
      .from('chat_conversations')
      .select('*')
      .eq('id', conversationId)
      .single();

    if (error) {
      console.error('Error fetching conversation:', error);
    } else if (data) {
      setConversation({
        ...data,
        messages: (data.messages as any) || []
      });
    }
    setIsLoading(false);
  };

  if (isLoading) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <div className="flex items-center justify-center h-full">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  if (!conversation) return null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Chat Conversation</SheetTitle>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Metadata Section */}
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Status:</span>
              <Badge variant={conversation.status === 'active' ? 'default' : 'secondary'}>
                {conversation.status}
              </Badge>
              {conversation.appointment_requested && (
                <Badge variant="default" className="bg-green-600">
                  Appointment Booked ✓
                </Badge>
              )}
            </div>

            {conversation.user_info?.name && (
              <div className="text-sm">
                <span className="font-medium">Name:</span> {conversation.user_info.name}
              </div>
            )}

            {conversation.user_info?.email && (
              <div className="text-sm">
                <span className="font-medium">Email:</span> {conversation.user_info.email}
              </div>
            )}

            <div className="text-sm">
              <span className="font-medium">Started:</span>{' '}
              {formatDistanceToNow(new Date(conversation.started_at), { addSuffix: true })}
            </div>

            <div className="text-sm">
              <span className="font-medium">Last Activity:</span>{' '}
              {formatDistanceToNow(new Date(conversation.last_activity_at), { addSuffix: true })}
            </div>

            <div className="text-sm text-muted-foreground">
              <span className="font-medium">Session ID:</span> {conversation.session_id}
            </div>
          </div>

          <Separator />

          {/* Messages Section */}
          <div className="space-y-4">
            <h3 className="font-semibold">Conversation</h3>
            <div className="space-y-4">
              {conversation.messages.map((message, index) => (
                <div
                  key={index}
                  className={`flex gap-3 ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {message.role === 'assistant' && (
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Bot className="h-4 w-4 text-primary" />
                    </div>
                  )}
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                      message.role === 'user'
                        ? 'bg-primary text-primary-foreground'
                        : 'bg-muted text-foreground'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-wrap">{message.content}</p>
                  </div>
                  {message.role === 'user' && (
                    <div className="h-8 w-8 rounded-full bg-secondary flex items-center justify-center flex-shrink-0">
                      <User className="h-4 w-4" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
